import React from 'react';
import { PaymentDetails } from '../../../API/GoMoto';
import { ScrollableBody } from '../../Layout/ScrollableBody';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { CTAButton } from '../../../controls/ButtonBase';
import { useLocale, useLocalization } from '../../../context/Localization/localization';
import css from './PaymentStatus.module.scss';
import { formatAsCurrency } from '../../../helpers/Number';
import { useDisplayLocalizedVehicle, DisplayMake } from '../../../helpers/displayVehicle';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { GMAppointmentName } from '../../../controls/AppointmentName';
import { ContactPreferenceEnum, useAppointmentState } from '../../../context/Appointment/appointment';
import { GMButtonGroup } from '../../../controls/ButtonGroup';
import { Locales } from '../../../context/Localization/localization.types';

interface IProps {
    onGetKeys: () => void;
    paymentDetails?: PaymentDetails | null;
    paymentComplete: boolean;
    paymentIdLabel: string;
}

export const PaymentStatus: React.FC<IProps> = ({ onGetKeys, paymentComplete, paymentDetails, paymentIdLabel }) => {
    const appointment = useAppointmentState();
    const hasEarlierPayment: boolean = !!appointment?.paymentTransactionId;
    const {
        kioskType,
        dealer: { country, customerTextingActive },
    } = useKioskState();
    const Localization = useLocalization();
    const locale = useLocale();
    const displayLocalizedVehicle = useDisplayLocalizedVehicle();
    const effectivePaymentDetails: PaymentDetails | null = paymentDetails ?? (
        hasEarlierPayment ? {
            id: appointment.paymentTransactionId,
            status: appointment.paymentStatus,
            amount: (appointment.serviceTotal ?? 0) * 100,
            created: appointment.paymentDateTime ? new Date(appointment.paymentDateTime)
                .toISOString() : null,
            type: appointment.paymentCardType,
            cardLast4: appointment.paymentCardNumber?.replace(/^[^0-9]+/, ''),
            errorMessage: appointment.paymentMessage,
            approvalNumber: appointment.paymentApprovalCode,
            merchantId: appointment.paymentMerchantId,
        } as PaymentDetails : null
    );
    const noPaymentDue: boolean = appointment.paymentStatus === 'no payment due' || appointment.paymentStatus === 'marked paid';
    const localizedPaymentStatus: string | undefined = effectivePaymentDetails?.status?.trim()
        .toLowerCase() === 'paid' ? Localization.Paid : effectivePaymentDetails?.status;
    const showGetKeys: boolean = (paymentComplete || noPaymentDue)
        && appointment.keyStatus === 'slot_assigned'
        && !['completed', 'loaner returned', 'rental returned'].includes(appointment.status);
    /*
     * Reflect the pickup contact preference in the receipt verbiage. Text variants are only used
     * when the dealer has texting enabled, since otherwise no text is actually sent.
     */
    const contactPreference = appointment.pickupContactPreference || appointment.contactPreference;
    const paymentReceiptMessage: string = customerTextingActive && contactPreference === ContactPreferenceEnum.Both
        ? Localization.ThanksForYourPaymentTextAndEmail
        : customerTextingActive && contactPreference === ContactPreferenceEnum.Text
            ? Localization.ThanksForYourPaymentText
            : Localization.ThanksForYourPaymentEmail;

    return (
        <ScrollableBody
            hideAccessibilityButton
            proceedButtons={
                showGetKeys ? (
                    <GMButtonGroup
                        alignment={'center'}
                        buttons={[
                            (
                                <CTAButton
                                    styleType={'primary'}
                                    label={Localization.GetKeys}
                                    onClick={onGetKeys}
                                    kioskOptions={{ square: true }}
                                    title={Localization.ClickToRetrieveKeys}
                                    id={Localization.ClickToRetrieveKeys}
                                    ariaLabel={Localization.ClickToRetrieveKeys}
                                />
                            ),
                        ]}
                    />
                ) : undefined
            }
        >
            <>
                <h2 className={css.title}>{!paymentComplete && (noPaymentDue || hasEarlierPayment) ? Localization.NoPaymentDueExc : Localization.PaymentSuccessful}</h2>
                <p>{!paymentComplete && (noPaymentDue || hasEarlierPayment) ? Localization.APaymentIsNotDue : paymentReceiptMessage}</p>
                <br />
                {showGetKeys ? (
                    <>
                        <h2 className={css.title}>{kioskType === 'outdoor' ? Localization.AtTheLockerNow : Localization.AtTheKioskNow}</h2>
                        <p>{Localization.SelecteGetKeysBelow}</p>
                    </>
                ) : null}
                {effectivePaymentDetails ? (
                    <div className={css.paymentInfo}>
                        <div className={css.name}>
                            {Localization.Name}
                            {':'}
                            {' '}
                            <b>
                                <GMAppointmentName
                                    appointment={appointment}
                                    fullName
                                    lastFirst={false}
                                />
                            </b>
                        </div>
                        <table>
                            <tbody>
                                <tr>
                                    <td className={css.leftCol} colSpan={2}>
                                        {Localization.RONumber} {appointment.roNumber}
                                    </td>
                                </tr>
                                {appointment.year && DisplayMake(appointment) ? (
                                    <tr>
                                        <td className={css.leftCol} colSpan={2}>
                                            {displayLocalizedVehicle(appointment)}
                                        </td>
                                    </tr>
                                ) : null}
                                <tr>
                                    <td className={css.leftCol}>
                                        <b>{Localization.PaymentAmount}</b>
                                    </td>
                                    <td className={css.leftCol}>
                                        <b>{formatAsCurrency(effectivePaymentDetails.amount / 100, true, country)}</b>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={css.leftCol}>{Localization.DateTime}</td>
                                    <td className={css.leftCol}>
                                        {dayjs(effectivePaymentDetails.created ?? undefined)
                                            .locale(locale === Locales.es ? 'es-us' : locale === Locales.fr ? 'fr' : 'en')
                                            .format(locale === Locales.fr ? 'DD MMM YYYY H [h] MM' : locale === Locales.es ? 'DD MMM YYYY h:mmA' : 'MMM DD, YYYY h:mmA')}
                                    </td>
                                </tr>
                                <tr>
                                    <td className={css.leftCol}>{Localization.PaymentType}</td>
                                    <td className={classNames(css.rightCol, css.uppercase)}>
                                        <p>{effectivePaymentDetails.type}</p>
                                    </td>
                                </tr>
                                {effectivePaymentDetails.cardLast4 ? (
                                    <tr>
                                        <td className={css.leftCol}>{Localization.CardNumber}</td>
                                        <td className={css.rightCol}>{'************'}{effectivePaymentDetails.cardLast4}</td>
                                    </tr>
                                ) : null}
                                <tr>
                                    <td className={css.leftCol}>{Localization.PaymentStatus}</td>
                                    <td className={classNames(css.rightCol, css.capitalize)}>{localizedPaymentStatus}</td>
                                </tr>
                                <tr>
                                    <td className={css.leftCol}>{paymentIdLabel}</td>
                                    <td className={css.rightCol}>{effectivePaymentDetails.id}</td>
                                </tr>
                                {effectivePaymentDetails.merchantId ? (
                                    <tr>
                                        <td className={css.leftCol}>{Localization.AcceptorID}</td>
                                        <td className={css.rightCol}>{effectivePaymentDetails.merchantId}</td>
                                    </tr>
                                ) : null}
                                {effectivePaymentDetails.approvalNumber ? (
                                    <tr>
                                        <td className={css.leftCol}>{Localization.ApprovalNumber}</td>
                                        <td className={css.rightCol}>{effectivePaymentDetails.approvalNumber}</td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                        <i>
                            <p className={css.disclaimer}>{Localization.PaymentDisclaimer}</p>
                        </i>
                    </div>
                ) : null}
            </>
        </ScrollableBody>
    );
};
